import React from 'react'

import {
  Box,
  capitalize,
  FormControlLabel,
  formControlLabelClasses,
  getTypographyUtilityClass,
  styled,
  unstable_composeClasses,
} from '@mui/material'
import classnames from 'classnames'

import type { SimpleFieldWrapperProps } from '../Field/Field.types'
import TooltipIcon from '../Tooltip/TooltipIcon'
import { getSimpleFieldWrapperUtilityClass } from './simpleFieldWrapperClasses'

const SimpleFieldWrapperRoot = styled(FormControlLabel, {
  name: 'SimpleFieldWrapper',
  slot: 'Root',
  overridesResolver: (props, styles) => [
    styles.root,
    styles[`size${capitalize(props.size || 'medium')}`],
    props.error && styles.error,
  ],
  shouldForwardProp: (prop) => prop !== 'size' && prop !== 'variant' && prop !== 'responsive' && prop !== 'tooltip',
})<SimpleFieldWrapperProps>(({ theme, size, error, tooltip }) => ({
  marginRight: 0,

  ...(tooltip && {
    [`& .${formControlLabelClasses.asterisk}`]: {
      display: 'none',
    },
  }),

  [`& .${getTypographyUtilityClass('root')}`]: {
    ...(error && {
      color: theme.palette.error.main,
    }),

    ...(size === 'large' && {
      fontSize: theme.typography.pxToRem(14),

      '& label': {
        fontSize: theme.typography.pxToRem(14),
      },
    }),
  },
}))

const useUtilityClasses = (ownerState: Partial<SimpleFieldWrapperProps>) => {
  const slots = {
    root: ['root', `size${capitalize(ownerState.size || 'medium')}`, ownerState.error && 'error'],
  }

  return unstable_composeClasses(slots, getSimpleFieldWrapperUtilityClass, ownerState.classes)
}

const SimpleFieldWrapper = (props: SimpleFieldWrapperProps) => {
  const { className, classes, size, slotProps, error, LabelProps, tooltip, label, ...rootProps } = props

  const slotClasses = useUtilityClasses({ classes, size, error })

  const controlRequired =
    React.isValidElement<{ required?: boolean }>(rootProps.control) && rootProps.control.props.required

  const renderedLabel = tooltip ? (
    <Box component="span" display="inline-flex" alignItems="center">
      {label}
      {controlRequired && (
        <Box component="span" aria-hidden>
          &thinsp;{'*'}
        </Box>
      )}
      <TooltipIcon tooltip={tooltip} component="span" display="inline-flex" />
    </Box>
  ) : (
    label
  )

  return (
    <SimpleFieldWrapperRoot
      {...rootProps}
      tooltip={tooltip}
      label={renderedLabel}
      size={size}
      className={classnames(slotClasses.root, className)}
      slotProps={{
        ...slotProps,
        typography: {
          ...slotProps?.typography,
          ...LabelProps,
          variant: 'body2',
        },
      }}
    />
  )
}

export default React.memo(SimpleFieldWrapper) as typeof SimpleFieldWrapper
